/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useState, useEffect } from 'react';
import './css/Table.css';
import Button from '../components/Button';
import Modal from './Modal';
import { IQuestion, IQuestionnaire } from '../types/questionnaire';
import { useNavigate } from 'react-router-dom';
import { JobsService, UserData } from '../api/JobsService';
import { getCompanyIdFromToken } from '../utils/tokenHelper';
import Toast from './Toast';

interface TableProps {
  data: IQuestionnaire[];
  activeJobsCounts?: Record<string, number>;
  onAddToJob?: (jobNumber: number, itemId: string) => void;
  onPreview?: (itemId: string) => void;
  onStatusChange?: (itemId: string, newStatus: boolean) => void;
}

const Table: React.FC<TableProps> = ({ 
  data, 
  activeJobsCounts = {},
  onAddToJob, 
  onPreview, 
  onStatusChange 
}) => {
  const navigate = useNavigate();
  const [users, setUsers] = useState<Record<number, UserData>>({});
  const [loadingUsers, setLoadingUsers] = useState(true);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const companyId = getCompanyIdFromToken();
        if (!companyId) {
          console.error('No company ID found in token');
          setLoadingUsers(false);
          return;
        }

        const usersList = await JobsService.getUsers(companyId);
        const usersMap = usersList.reduce((acc, user) => {
          acc[user.id] = user;
          return acc;
        }, {} as Record<number, UserData>);
        setUsers(usersMap);
      } catch (error) {
        console.error('Error fetching users:', error);
      } finally {
        setLoadingUsers(false);
      }
    };

    fetchUsers();
  }, []);

  const getUserDisplayName = (userId: number | undefined) => {
    if (!userId) return 'Unknown';
    if (loadingUsers) {
      return <small className="text-muted">Loading...</small>;
    }
    
    const user = users[userId];
    if (!user) {
      return 'Unknown';
    }
    
    return `${user.contactInfo.firstName} ${user.contactInfo.lastName}`;
  };
  const [hasShownDeactivateToast, setHasShownDeactivateToast] = useState(false);

  // Show toast only on first deactivation
  const handleToastOnDeactivate = () => {
    if (!hasShownDeactivateToast) {
      setHasShownDeactivateToast(true);
    }
  };

  const handleEdit = (questionnaireId: string) => {
    navigate(`/recruiter/questionnaire-builder/${questionnaireId}`);
  };

  const handleClone = (questionnaire: IQuestionnaire) => {
    navigate('/recruiter/questionnaire-builder', {
      state: {
        isCloning: true,
        cloneData: {
          questionnaireName: `${questionnaire.questionnaireName} (Copy)`,
          contentType: questionnaire.contentType,
          questions: questionnaire.questions.map(q => ({
            text: q.text || q.questionText,
            type: q.type,
            required: q.required,
            options: q.options
          }))
        }
      }
    });
  };

  const formatDate = (date: string | Date) => {
    return new Intl.DateTimeFormat('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric'
    }).format(new Date(date));
  };

  return (
    <div className={`table-responsive card ${data.length === 1 ? 'single-row' : ''}`}>
      <table className="table mb-0">
        <thead>
          <tr className="bg-light text-align-left">
            <th>Name</th>
            <th>Created by</th>
            <th>Status</th>
            <th>Questions</th>
            <th>Active Jobs</th>
            {/* <th>Completed</th> */}
            <th>Action</th>
          </tr>
        </thead>
        <tbody>
          {data.map((item, index) => (
            <tr key={item.questionnaireId}>
              <td>
                <div className="d-flex flex-column text-align-left">
                  <span>{item.questionnaireName}</span>
                    <div className='d-flex align-items-center mt-1'>
                    <span>
                      <img 
                        src={item.contentType === 'Video' ? '/svgs/video.svg' : item.contentType === 'Audio' ? '/svgs/mic.svg' : ''}
                        alt={item.contentType} 
                        width="16" 
                        height="16" 
                        className="text-secondary" 
                      />
                    </span>
                        <small className="text-muted ml-2"> {item.contentType} </small>
                    </div>
                </div>
              </td>
              <td>
                <div>
                  <div className="text-align-left">{getUserDisplayName(item.userId)}</div>
                  <div className="text-secondary small text-align-left">on {formatDate(item.createdAt)}</div>
                </div>
              </td>
              <td>
                <div className='d-flex align-items-center'>
                    <div className={`status-dot ${item.active === true ? 'active' : 'inactive'} me-2`}></div>
                    {item.active ? 'Active' : 'Inactive'}
                </div>
              </td>
              <td className='text-align-left'>{item.questions.length}</td>
              <td className='text-align-left'>
                {activeJobsCounts[item.questionnaireId] || 0}
              </td>
              {/* TODO: Completed - Once completed count is available, readd this column*/}
              {/* <td className='text-align-left'>45</td> */}
              <td>
                <div className="d-flex align-items-center gap-2">
                    <div className="me-2">
                        <Button 
                            text={`Add to Job`} 
                            variant="outline-primary" 
                            onClick={() => onAddToJob?.(index + 1, item.questionnaireId)}
                            id={`add-to-job-button-${item.questionnaireId}`}
                        />
                    </div>
                    <div className="ml-2">
                        <button 
                          className='btn btn-outline-primary ml-2' 
                          data-toggle="modal" 
                          data-target={`#modal-${item.questionnaireId}`}
                          id={`preview-questionnaire-button-${item.questionnaireId}`}
                        >
                          View
                        </button>
                        <Modal 
                          id={item.questionnaireId} 
                          questionnaireName={item.questionnaireName}
                          questions={item.questions as IQuestion[]}
                          contentType={item.contentType}
                        />
                    </div>
                    
                    <div className="dropdown ml-3">
                        <button 
                            className="btn btn-link btn-sm text-secondary" 
                            type="button" 
                            data-toggle="dropdown"
                        >
                            <i className="fas fa-ellipsis-h"></i>
                        </button>

                        <div className="dropdown-menu" role='menu'>
                          <a 
                            className="dropdown-item" 
                            href="#"
                            id={`edit-questionnaire-button-${item.questionnaireId}`}
                            onClick={(e) => {
                              e.preventDefault();
                              handleEdit(item.questionnaireId);
                            }}
                          >
                            <i className="fa fa-edit mr-2"></i>Edit
                          </a>
                          <a 
                            className="dropdown-item" 
                            href="#"
                            id={`clone-questionnaire-button-${item.questionnaireId}`}
                            onClick={(e) => {
                              e.preventDefault();
                              handleClone(item);
                            }}
                          >
                            <i className="fa fa-clone mr-2"></i>Clone
                          </a>
                          
                          {item.active ? (
                            <a 
                              className="dropdown-item text-danger"
                              href="#"
                              id={`deactivate-questionnaire-button-${item.questionnaireId}`}
                              onClick={(e) => {
                                e.preventDefault();
                                onStatusChange?.(item.questionnaireId, false);
                                handleToastOnDeactivate();
                              }}
                            >
                              <i className="fa fa-circle mr-2"></i>
                              Deactivate
                            </a>
                          ) : (
                            <a 
                              className="dropdown-item text-success"
                              href="#"
                              id={`reactivate-questionnaire-button-${item.questionnaireId}`}
                              onClick={(e) => {
                                e.preventDefault();
                                onStatusChange?.(item.questionnaireId, true);
                              }}
                            >
                              <i className="fa fa-circle mr-2"></i>
                              Reactivate
                            </a>
                          )}
                        </div>
                    </div>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {/* The toast is shown when hasShownDeactivateToast is true */}
      {hasShownDeactivateToast && (
        <Toast 
          isOpen={true}
          onClose={() => {}}
          message={`Inactive Questionnaires maintain their association with jobs, <strong>however no Interviews will be sent when candidates apply.</strong>`}
          type="warning"
          title="Questionnaire Deactivated"
          autoClose={true}
          duration={10000}
        />
      )}
    </div>
  );
};

export default Table;
